import React from "react";
import GooglePayButton from "@google-pay/button-react";
import { DigitalPaymentData } from "types/digitalPaymentData";
import IamApi from "api/IamApi";
import FundingOption from "constants/FundingOption";
import { PaymentIntentPrepareRequest } from "types/Api/Payment/paymentIntentPrepareRequest";
import { IGooglePayConfig } from "types/digitalPaymentConfigurations";
import { setAppLoading } from "actions/appStateActions";
import { useDispatch, useSelector } from "react-redux";
import { getSelectedAmountOptionKey } from "selectors/paymentIntent";
import { LIVE } from "constants/realm";
import { envs } from "config";
import {
  getGoogleCardNetworkByShortCode,
  getCommonCardNetworks,
  getDomain,
} from "utilities/helper";
import { getPayableFromSession } from "api/IamApi/entities/ezpay";
import { setPayableSession } from "actions/payableSessionActions";
import { IPayableSessionData } from "types/payableSession";
import { useNavigate } from "react-router";
import routing from "routing";

export interface IPayableGooglePayButtonWrapperProps {
  country: string;
  currencyCode: string;
  clientId: string;
  paymentIntentId: string;
  amount: number;
  reviewDigitalPayment: (paymentData: DigitalPaymentData) => void;
  verifyPaymentAmount: () => { isValid: boolean };
  onPrepareIntentError: (
    err: any,
    prepareRequest: PaymentIntentPrepareRequest
  ) => void;
  config: IGooglePayConfig;
  realm: string;
  methodOfPayments: string[];
}

export const PayableGooglePayButtonWrapper = (
  props: IPayableGooglePayButtonWrapperProps
) => {
  const {
    country,
    currencyCode,
    amount,
    reviewDigitalPayment,
    verifyPaymentAmount,
    onPrepareIntentError,
    config,
    realm,
    methodOfPayments,
  } = props;

  const navigate = useNavigate();

  const dispatch = useDispatch();
  const selectedOptionKey = useSelector(getSelectedAmountOptionKey);

  const supportedCcNetworkForGooglePay: string[] = methodOfPayments.map(
    (method) => {
      return getGoogleCardNetworkByShortCode(method);
    }
  );

  const supportedCcNetworksForGoogle = getCommonCardNetworks(
    supportedCcNetworkForGooglePay
  );

  const isProductionBuild = process.env.REACT_APP_ENV === envs.PRODUCTION;
  const environment =
    isProductionBuild && realm.toLowerCase() === LIVE.toLowerCase() ? "PRODUCTION" : "TEST";

  const paymentDataRequest: google.payments.api.PaymentDataRequest = {
    merchantInfo: {
      merchantName: config.merchantName,
      merchantId: config.merchantId,
    },
    apiVersion: 2,
    apiVersionMinor: 0,
    emailRequired: true,
    shippingAddressRequired: true,
    shippingAddressParameters: {
      allowedCountryCodes: ["US", "CA"],
      phoneNumberRequired: true,
    },
    allowedPaymentMethods: [
      {
        type: "CARD",
        parameters: {
          allowedAuthMethods: ["PAN_ONLY", "CRYPTOGRAM_3DS"],
          allowedCardNetworks: supportedCcNetworksForGoogle,
        },
        tokenizationSpecification: {
          type: "DIRECT",
          parameters: {
            protocolVersion: "ECv2",
            publicKey: config.publicKey,
          },
        },
      },
    ],
    transactionInfo: {
      countryCode: country,
      currencyCode: currencyCode,
      totalPriceStatus: "FINAL",
      totalPrice: amount.toFixed(2),
    },
  };

  const handleButtonClick = (event: any) => {
    const isValidAmount = verifyPaymentAmount();
    if (!isValidAmount.isValid) {
      event.preventDefault();
      dispatch(setAppLoading(false));
    }
  };
  const handleLoadPaymentData = async (
    payload: google.payments.api.PaymentData
  ) => {
    dispatch(setAppLoading(true));

    const token = window.btoa(payload.paymentMethodData.tokenizationData.token);

    const prepareRequest: PaymentIntentPrepareRequest = {
      paymentAmount: amount,
      ...(selectedOptionKey ? { selectedOptionKey } : {}),
      fundingOption: FundingOption.GooglePay,
      digitalWallet: {
        paymentToken: token,
        cardType: payload.paymentMethodData.info?.cardNetwork,
      },
    };

    await IamApi.ezpay
      .postPayablePrepareIntent(prepareRequest)
      .then(async (response) => {
        const paymentData: DigitalPaymentData = {
          payer: payload.shippingAddress?.name ?? "",
          email: payload.email ?? "",
          mobileNumber: payload.shippingAddress?.phoneNumber,
          paymentToken: token,
          cardType: payload.paymentMethodData.info?.cardNetwork,
          paymentMethod: FundingOption.GooglePay,
          serviceFee: Number(response.data.feeTransactionAmount),
        };
        reviewDigitalPayment(paymentData);
        const session = await getPayableFromSession(getDomain());
        dispatch(setPayableSession(session));
        navigate(routing.payableReview.index());
        dispatch(setAppLoading(false));
      })
      .catch((err) => {
        onPrepareIntentError(err, prepareRequest);
        dispatch(setAppLoading(false));
      })
      .catch((err) => {
        console.error("Error fetching payable session after prepare", err);
        throw err;
      });
  };

  const handleError = (error: Error | google.payments.api.PaymentsError) => {
    console.log(error);
    dispatch(setAppLoading(false));
  };

  return (
    <GooglePayButton
      style={{
        height: "50px",
        width: "100%",
        alignSelf: "center",
        outline: "none !important",
      }}
      environment={environment}
      paymentRequest={paymentDataRequest}
      onLoadPaymentData={handleLoadPaymentData}
      onError={handleError}
      onClick={handleButtonClick}
      buttonType="pay"
      buttonColor="white"
      buttonSizeMode="fill"
    />
  );
};
